import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import HeaderWithoutLang from "../HeaderWithoutLang";
import Footer from "../Footer";
import "./inventoryFinancing.css";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { CREDIT_API_URL } from "../../constants";
import { formatToIndianRupee } from "../../utility/FormatToIndianRupee";
import OtpVerificationModal from "../../utility/OtpVerificationModal";
import getClientInfo from '../../utility/getClientInfo';
import showToast from "../../utility/showToast";
import NotificationPopup from "../../utility/NotificationPopup";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import usePageTitle from "../../hooks/usePageTitle";
import { decrypt } from "../../utility/ClientEncryption";
import useUtmStore from '../../store/useUtmStore';
const initialInvoiceDetails = {
  invoiceNumber: "",
  invoiceDate: "",
  dueDate: "",
  irnNumber: "",
  totalAmount: "",
  cgst: "",
  igst: "",
  sgst: "",
  sellerGST: "",
  sellerPAN: "",
  buyerGST: "",
  buyerPAN: "",
};

const InventoryFinancingApplicationPage = () => {
  const { t } = useTranslation("inventoryFinancingPage");
  const location = useLocation();
  const navigate = useNavigate();

  // Storage key for persisting application state
  const STORAGE_KEY = 'inventoryFinancingApplicationState';
  const DOCUMENT_ID_STORAGE_KEY = 'inventoryFinancingDocumentId';
  const utm = useUtmStore(state => state.utm || {});
  // Helper function to load state from localStorage
  const loadStateFromStorage = () => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (error) {
      console.error('Error loading state from localStorage:', error);
    }
    return null;
  };

  // Helper function to save state to localStorage
  const saveStateToStorage = (state) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving state to localStorage:', error);
    }
  };

  // Helper function to clear state from localStorage
  const clearStateFromStorage = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(DOCUMENT_ID_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing state from localStorage:', error);
    }
  };

  // Helper function to save documentId to localStorage
  const saveDocumentIdToStorage = (documentId) => {
    try {
      if (documentId) {
        localStorage.setItem(DOCUMENT_ID_STORAGE_KEY, documentId);
      }
    } catch (error) {
      console.error('Error saving documentId to localStorage:', error);
    }
  };

  // Helper function to get documentId from location.state or localStorage
  const getDocumentId = () => {
    // Priority: 1. location.state, 2. localStorage
    if (location.state?.documentId || location.state?.data?.documentId) {
      return location.state.documentId || location.state.data.documentId;
    }
    try {
      return localStorage.getItem(DOCUMENT_ID_STORAGE_KEY);
    } catch (error) {
      console.error('Error getting documentId from localStorage:', error);
      return null;
    }
  };

  // Check for documentId on mount and redirect if missing
  useEffect(() => {
    // Skip documentId check if coming from dashboard with skipToStep (continuing existing application)
    if (location.state?.skipToStep && (location.state?.leadId || location.state?.invoiceNo)) {
      console.log('Continuing existing application from dashboard, skipping documentId check');
      return;
    }

    const documentId = getDocumentId();
    
    if (!documentId) {
      // No documentId found, redirect to inventory financing page
      console.warn('No documentId found, redirecting to inventory financing page');
      navigate('/inventory-financing', { replace: true });
      return;
    }

    // Save documentId to localStorage if it came from location.state
    if (location.state?.documentId || location.state?.data?.documentId) {
      saveDocumentIdToStorage(documentId);
    }
  }, [location.state, navigate]);

  // Helper function to map API response to invoice details format
  const mapApiResponseToInvoiceDetails = (apiData) => {
    if (!apiData) return initialInvoiceDetails;
    
    return {
      invoiceNumber: apiData.invoiceNumber || "",
      invoiceDate: apiData.invoiceDate || "",
      dueDate: apiData.dueDate || "",
      irnNumber: apiData.irnNumber || "",
      totalAmount: apiData.totalAmount || "",
      cgst: apiData.cgstAmount || apiData.cgst || "",
      igst: apiData.igstAmount || apiData.igst || "",
      sgst: apiData.sgstAmount || apiData.sgst || "",
      sellerGST: apiData.sellerGST || "",
      sellerPAN: apiData.sellerPAN || "",
      buyerGST: apiData.buyerGST || "",
      buyerPAN: apiData.buyerPAN || "",
    };
  };

  // Reset state when a new invoice is uploaded (new application)
  useEffect(() => {
    // If location.state has invoiceDetails and isNewApplication flag, clear previous state
    if (location.state?.invoiceDetails && location.state?.isNewApplication) {
      // Clear all previous state
      clearStateFromStorage();
      
      // Only reset to step 1 if we're not skipping to a specific step
      // If skipToStep is provided (e.g., 3 for leadCreated), don't reset the step
      if (!location.state?.skipToStep) {
        setStep(1);
      }
      
      // Reset all form states
      const mappedData = mapApiResponseToInvoiceDetails(location.state.invoiceDetails);
      setInvoiceDetails({ ...initialInvoiceDetails, ...mappedData });
      setConsent1(false);
      setCompanyDetails({
        businessType: "",
        legalName: "",
        registeredAddress: "",
        natureOfWork: "",
        annualTurnoverSlab: "",
        panNumber: "",
        gstActive: "",
        contactName: "",
        contactEmail: "",
        contactMobile: ""
      });
      setCompanyConsent(false);
      setLoanOffer({
        invoiceAmount: "",
        approvedLoanAmount: "",
        requiredLoanAmount: "",
        expectedPaymentDate: "",
        rateOfInterest: "12%",
        durationOfLoan: "",
        interestToBePaid: "",
        totalAmountToBePaid: ""
      });
      setLoanOfferConsent(false);
      // Don't reset leadId if it's provided in location.state (for skipToStep case)
      if (!location.state?.leadId) {
        setLeadId("");
      }
      setShowOtpModal(false);
      setShowThankYou(false);
      setErrorMessage("");
      setOtpError("");
      setRequiredLoanAmountError("");
    }
  }, [location.state?.isNewApplication, location.state?.invoiceDetails, location.state?.skipToStep, location.state?.leadId]);

  // Start directly from invoice details step
  const [step, setStep] = useState(() => {
    // Priority: 1. location.state.skipToStep, 2. Saved state, 3. Default to 1
    if (location.state?.skipToStep) {
      return location.state.skipToStep;
    }
    const saved = loadStateFromStorage();
    return saved?.step || 1;
  });

  const [invoiceDetails, setInvoiceDetails] = useState(() => {
    // Priority: 1. location.state, 2. Saved state, 3. Initial state
    if (location.state && location.state.invoiceDetails) {
      const mappedData = mapApiResponseToInvoiceDetails(location.state.invoiceDetails);
      return { ...initialInvoiceDetails, ...mappedData };
    }
    const saved = loadStateFromStorage();
    if (saved?.invoiceDetails) {
      return { ...initialInvoiceDetails, ...saved.invoiceDetails };
    }
    return initialInvoiceDetails;
  });
  const [consent1, setConsent1] = useState(() => {
    const saved = loadStateFromStorage();
    return saved?.consent1 || false;
  });
  const [consent2, setConsent2] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Step 2: Company Details state
  const [companyDetails, setCompanyDetails] = useState(() => {
    const defaultCompanyDetails = {
      businessType: "",
      legalName: "",
      registeredAddress: "",
      natureOfWork: "",
      annualTurnoverSlab: "",
      panNumber: "",
      gstActive: "",
      contactName: "",
      contactEmail: "",
      contactMobile: ""
    };
    // Load from saved state if available
    const saved = loadStateFromStorage();
    if (saved?.companyDetails) {
      return { ...defaultCompanyDetails, ...saved.companyDetails };
    }
    return defaultCompanyDetails;
  });
  const [companyConsent, setCompanyConsent] = useState(() => {
    const saved = loadStateFromStorage();
    return saved?.companyConsent || false;
  });
  const [otpError, setOtpError] = useState("");
  const [isProcessingStep2, setIsProcessingStep2] = useState(false);

  // Step 3: Loan Offer state
  const [loanOffer, setLoanOffer] = useState(() => {
    const defaultLoanOffer = {
      invoiceAmount: "",
      approvedLoanAmount: "",
      requiredLoanAmount: "",
      expectedPaymentDate: "",
      rateOfInterest: "12%",
      durationOfLoan: "",
      interestToBePaid: "",
      totalAmountToBePaid: ""
    };
    // Load from saved state if available
    const saved = loadStateFromStorage();
    if (saved?.loanOffer) {
      return { ...defaultLoanOffer, ...saved.loanOffer };
    }
    return defaultLoanOffer;
  });
  const [loanOfferConsent, setLoanOfferConsent] = useState(() => {
    const saved = loadStateFromStorage();
    return saved?.loanOfferConsent || false;
  });
  const [requiredLoanAmountError, setRequiredLoanAmountError] = useState("");
  const [leadId, setLeadId] = useState(() => {
    // Priority: 1. location.state, 2. Saved state, 3. Default to empty
    if (location.state?.leadId) {
      return location.state.leadId;
    }
    const saved = loadStateFromStorage();
    return saved?.leadId || "";
  });
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [decryptedMobileNumber, setDecryptedMobileNumber] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [isProcessingStep3, setIsProcessingStep3] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(30);
  const [submittedDate, setSubmittedDate] = useState("");
  
  // Anchor not found popup state
  const [showAnchorNotFoundPopup, setShowAnchorNotFoundPopup] = useState(false);
  const [anchorStatusMessage, setAnchorStatusMessage] = useState("");
  const [anchorPresenceStatus, setAnchorPresenceStatus] = useState(null);
  const [buyerDetails, setBuyerDetails] = useState({
    buyerGst: "",
    buyerPan: "",
    contactPersonName: "",
    contactMobile: "",
    companyName: ""
  });
  const [buyerDetailsErrors, setBuyerDetailsErrors] = useState({});
  const [isSubmittingBuyerDetails, setIsSubmittingBuyerDetails] = useState(false);
  
  // Notification popup state
  const [showNotification, setShowNotification] = useState(false);
  const [showPendingNotification, setShowPendingNotification] = useState(false);
  const [showRejectedNotification, setShowRejectedNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  // Handle skipToStep from location.state
  useEffect(() => {
    if (location.state?.skipToStep) {
      setStep(location.state.skipToStep);
    }
  }, [location.state?.skipToStep]);

  // Fetch invoice data when continuing from dashboard with skipToStep and invoiceNo
  useEffect(() => {
    const fetchInvoiceDataForDashboardContinue = async () => {
      // Only run if coming from dashboard with skipToStep: 3 and invoiceNo
      if (location.state?.skipToStep !== 3 || !location.state?.invoiceNo) {
        return;
      }

      const invoiceNoFromState = location.state.invoiceNo;
      const leadIdFromState = location.state.leadId;
      console.log('[InventoryFinancingApplication] Fetching invoice data for dashboard continuation, invoiceNo:', invoiceNoFromState);

      try {
        // Set leadId from state
        if (leadIdFromState) {
          setLeadId(leadIdFromState);
        }

        // Fetch invoice data using invoice lookup API
        const response = await axios.get(
          `${CREDIT_API_URL}invoices/lookup`,
          {
            params: {
              invoiceNo: invoiceNoFromState
            },
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            }
          }
        );
        
        if (response.status === 200 && response.data?.status === "Success" && response.data?.data?.[0]) {
          const invoiceData = response.data.data[0];
          console.log('[InventoryFinancingApplication] Invoice data fetched successfully:', invoiceData);
          
          // Set invoice details
          setInvoiceDetails({
            invoiceNumber: invoiceData.invoiceNumber || invoiceNoFromState,
            invoiceDate: invoiceData.invoiceDate || "",
            dueDate: invoiceData.dueDate || "",
            irnNumber: invoiceData.irnNumber || "",
            totalAmount: invoiceData.totalAmount || "",
            cgst: invoiceData.cgstAmount || invoiceData.cgst || "",
            igst: invoiceData.igstAmount || invoiceData.igst || "",
            sgst: invoiceData.sgstAmount || invoiceData.sgst || "",
            sellerGST: invoiceData.sellerGST || "",
            sellerPAN: invoiceData.sellerPAN || "",
            buyerGST: invoiceData.buyerGST || "",
            buyerPAN: invoiceData.buyerPAN || "",
          });

          // Extract documentId if available
          if (invoiceData.documentId || invoiceData.document_id) {
            const docId = invoiceData.documentId || invoiceData.document_id;
            console.log('[InventoryFinancingApplication] Setting documentId from invoice data:', docId);
            saveDocumentIdToStorage(docId);
          }

          // Extract and decrypt contact details if available
          if (invoiceData.contactMobile || invoiceData.contactEmail || invoiceData.contactPersonName) {
            try {
              let decryptedMobile = invoiceData.contactMobile;
              let decryptedEmail = invoiceData.contactEmail;

              // Decrypt mobile number if it exists
              if (invoiceData.contactMobile) {
                try {
                  console.log("Decrypting contactMobile from invoice lookup");
                  decryptedMobile = await decrypt(invoiceData.contactMobile.trim());
                  console.log("Decrypted mobile:", decryptedMobile);
                } catch (decryptError) {
                  console.warn("Failed to decrypt contactMobile, using as-is:", decryptError);
                  const plainNumber = invoiceData.contactMobile.replace(/\D/g, '');
                  if (plainNumber.length === 10) {
                    decryptedMobile = plainNumber;
                  }
                }
              }

              // Decrypt email if it exists
              if (invoiceData.contactEmail) {
                try {
                  console.log("Decrypting contactEmail from invoice lookup");
                  decryptedEmail = await decrypt(invoiceData.contactEmail.trim());
                  console.log("Decrypted email:", decryptedEmail);
                } catch (decryptError) {
                  console.warn("Failed to decrypt contactEmail, using as-is:", decryptError);
                  if (invoiceData.contactEmail.includes('@')) {
                    decryptedEmail = invoiceData.contactEmail;
                  }
                }
              }

              // Update company details with contact information
              setCompanyDetails(prev => ({
                ...prev,
                contactName: invoiceData.contactPersonName || prev.contactName || "",
                contactEmail: decryptedEmail || prev.contactEmail || "",
                contactMobile: decryptedMobile || prev.contactMobile || ""
              }));

              console.log("Company contact details updated from invoice lookup");
            } catch (error) {
              console.error("Error processing contact details from invoice lookup:", error);
            }
          }
        }
      } catch (error) {
        console.error('[InventoryFinancingApplication] Error fetching invoice data for dashboard continuation:', error);
        // Don't block the flow, just log the error
        showToast('Unable to fetch invoice details. Please verify and continue.', 'warning');
      }
    };

    fetchInvoiceDataForDashboardContinue();
  }, [location.state?.skipToStep, location.state?.invoiceNo]);

  // When skipping to step 3, ensure company details are available
  // If contactMobile is missing, fetch it from API using documentId and GST number
  useEffect(() => {
    const fetchCompanyDetailsIfNeeded = async () => {
      // Only run if we're on step 3 and skipped directly to it
      if (step !== 3 || location.state?.skipToStep !== 3) {
        return;
      }
      
      // Check if contactMobile is missing
      const hasMobile = companyDetails.contactMobile && companyDetails.contactMobile.trim() !== "";
      
      if (!hasMobile) {
        console.warn("Step 3 reached but contactMobile is missing. Attempting to fetch company details.");
        
        // First, try to load from localStorage
        const saved = loadStateFromStorage();
        if (saved?.companyDetails?.contactMobile && saved.companyDetails.contactMobile.trim() !== "") {
          console.log("Loading company details from localStorage");
          setCompanyDetails(prev => ({
            ...prev,
            ...saved.companyDetails
          }));
          return;
        }
        
        // If not in localStorage, fetch from API using documentId and GST number
        const documentId = getDocumentId();
        const sellerGST = invoiceDetails.sellerGST;
        
        if (documentId && sellerGST) {
          try {
            console.log("Fetching company details from API using documentId:", documentId, "and GST:", sellerGST);
            const gstVerifyResponse = await axios.post(
              `${CREDIT_API_URL}gst/verify`,
              {
                gstNumber: sellerGST,
                documentId: documentId
              },
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              }
            );

            if (gstVerifyResponse.status === 200 && gstVerifyResponse.data?.status === "Success") {
              const verificationResult = gstVerifyResponse.data?.data?.verificationResult;
              
              if (verificationResult) {
                // Map GST verification response to company details
                const address = verificationResult.address || {};
                const addressLine = [address.addressLine1, address.addressLine2]
                  .filter(Boolean)
                  .join(", ");
                
                // Convert industryType array to string
                const natureOfWork = verificationResult.industryType 
                  ? verificationResult.industryType.join(" & ")
                  : "";

                // Convert GST status
                const gstActive = address.gstinStatus === "Active" ? "Yes" : address.gstinStatus || "No";

                // Get first director name for contact name
                const contactName = verificationResult.directorNames && verificationResult.directorNames.length > 0
                  ? verificationResult.directorNames[0].trim()
                  : "";

                // Update company details with fetched data, preserving existing values
                setCompanyDetails(prev => ({
                  ...prev,
                  businessType: verificationResult.businessType || prev.businessType || "",
                  legalName: verificationResult.nameOfOrgAsPerGST || prev.legalName || "",
                  registeredAddress: addressLine || prev.registeredAddress || "",
                  natureOfWork: natureOfWork || prev.natureOfWork || "",
                  annualTurnoverSlab: verificationResult.annual_turnover || prev.annualTurnoverSlab || "",
                  gstActive: gstActive || prev.gstActive || "",
                  contactName: contactName || prev.contactName || "",
                  contactEmail: verificationResult.email || prev.contactEmail || "",
                  contactMobile: verificationResult.mobile || prev.contactMobile || ""
                }));
                
                console.log("Company details fetched successfully from API. Mobile:", verificationResult.mobile);
              } else {
                console.warn("GST verification response did not contain verificationResult");
              }
            } else {
              console.warn("GST verification API returned non-success status:", gstVerifyResponse.data);
            }
          } catch (error) {
            console.error("Error fetching company details from API:", error);
            console.error("Error details:", error.response?.data || error.message);
            // Don't show error to user here, it will be shown when trying to send OTP
          }
        } else {
          console.warn("Cannot fetch company details: missing documentId or sellerGST", { 
            documentId: documentId ? "present" : "missing", 
            sellerGST: sellerGST ? "present" : "missing" 
          });
        }
      }
    };

    fetchCompanyDetailsIfNeeded();
  }, [step, location.state?.skipToStep]);

  // Anchor presence flag constants
  const ANCHOR_PRESENCE_FLAG = {
    NOT_PRESENT: 0,      // Anchor company not found for buyer GST
    PENDING: 1,          // Anchor present but verification pending
    VERIFIED: 2,         // Anchor present and verified
    REJECTED: 3,         // Anchor present but rejected
  };

  // Get anchor status message based on flag
  const getAnchorStatusMessage = (flag) => {
    switch (flag) {
      case ANCHOR_PRESENCE_FLAG.NOT_PRESENT:
        return "Your buyer is currently not registered with us as an Anchor.";
      case ANCHOR_PRESENCE_FLAG.PENDING:
        return "Your buyer's anchor registration is pending verification.";
      case ANCHOR_PRESENCE_FLAG.REJECTED:
        return "Your buyer's anchor registration has been rejected.";
      default:
        return "Your buyer is not registered as an anchor.";
    }
  };

  // Helper function to format currency with Indian rupee symbol
  const formatCurrency = (amount) => {
    return formatToIndianRupee(amount);
  };

  // Helper function to convert yyyy-mm-dd string to dayjs object
  const stringToDayjs = (dateString) => {
    if (!dateString) return null;
    return dayjs(dateString);
  };

  // Helper function to convert dayjs object to yyyy-mm-dd string
  const dayjsToString = (date) => {
    if (!date || !date.isValid()) return '';
    return date.format('YYYY-MM-DD');
  };

  // Helper function to get last day of current month in dd-mm-yy format (for calculateDaysBetween)
  const getLastDayOfMonth = () => {
    const today = new Date();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const day = String(lastDay.getDate()).padStart(2, '0');
    const month = String(lastDay.getMonth() + 1).padStart(2, '0');
    const year = String(lastDay.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  // Helper function to get last day of current month in yyyy-mm-dd format (for date input)
  const getLastDayOfMonthYYYYMMDD = () => {
    const today = new Date();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const day = String(lastDay.getDate()).padStart(2, '0');
    const month = String(lastDay.getMonth() + 1).padStart(2, '0');
    const year = String(lastDay.getFullYear());
    return `${year}-${month}-${day}`;
  };

  // Helper function to calculate days between dates
  const calculateDaysBetween = (dateStr1, dateStr2) => {
    // Parse DD-MM-YY format
    const parseDate = (str) => {
      const [day, month, year] = str.split('-');
      const fullYear = parseInt(year) < 50 ? 2000 + parseInt(year) : 1900 + parseInt(year);
      return new Date(fullYear, parseInt(month) - 1, parseInt(day));
    };
    
    const date1 = parseDate(dateStr1);
    const date2 = parseDate(dateStr2);
    const diffTime = Math.abs(date2 - date1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Helper function to extract numeric value from currency string
  const extractNumericValue = (currencyStr) => {
    if (!currencyStr) return 0;
    
    // Convert to string and trim
    let str = String(currencyStr).trim();
    const originalStr = str;
    
    // Step 1: Remove currency prefixes (Rs., Rs, ₹, INR, $, USD)
    // Try multiple patterns to catch all variations
    str = str.replace(/^Rs\.?\s*/i, '');
    str = str.replace(/^Rs\s*/i, '');
    str = str.replace(/^INR\s*/i, '');
    str = str.replace(/^₹\s*/, '');
    str = str.replace(/^\$\s*/, '');
    str = str.replace(/^USD\s*/i, '');
    
    const afterCurrency = str;
    
    // Step 2: Remove all spaces
    str = str.replace(/\s+/g, '');
    
    // Step 3: Remove ALL commas (Indian number format uses commas as thousand separators)
    str = str.replace(/,/g, '');
    
    const afterComma = str;
    
    // Step 4: Extract digits and decimal point
    // Match: one or more digits, optionally followed by a decimal point and more digits
    const numberMatch = str.match(/(\d+)(?:\.(\d+))?/);
    
    if (numberMatch) {
      const integerPart = numberMatch[1] || '';
      const decimalPart = numberMatch[2] || '';
      str = integerPart + (decimalPart ? '.' + decimalPart : '');
    } else {
      // Fallback: extract all digits
      str = str.replace(/\D/g, '');
    }
    
    const value = parseFloat(str);
    
    // Debug log with all intermediate steps
    console.log("extractNumericValue - Full trace:", {
      original: originalStr,
      afterCurrencyRemoval: afterCurrency,
      afterCommaRemoval: afterComma,
      numberMatch: numberMatch ? [numberMatch[1], numberMatch[2]] : null,
      finalString: str,
      parsedValue: value,
      isValid: !isNaN(value) && value > 0
    });
    
    // If value is suspiciously small (< 100) and original had commas, try alternative extraction
    if (value < 100 && originalStr.includes(',')) {
      console.warn("Suspiciously small value detected, trying alternative extraction");
      // Try extracting all digits directly
      let altStr = originalStr.replace(/[^\d]/g, '');
      const altValue = parseFloat(altStr);
      if (altValue > value && altValue > 0) {
        console.log("Using alternative extraction:", altValue);
        return altValue;
      }
    }
    
    if (isNaN(value) || value <= 0) {
      console.error("ERROR: Invalid extraction from:", originalStr, "Result:", value);
      return 0;
    }
    
    return value;
  };

  // Fetch invoice lookup data when step 3 is reached
  useEffect(() => {
    if (step === 3 && invoiceDetails.invoiceNumber) {
      const fetchInvoiceLookup = async () => {
        try {
          const invoiceNo = invoiceDetails.invoiceNumber;
          const response = await axios.get(
            `${CREDIT_API_URL}invoices/lookup`,
            {
              params: {
                invoiceNo: invoiceNo
              },
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            }
          );

          if (response.status === 200 && response.data?.status === "Success") {
            const invoiceData = response.data?.data?.[0];
            const loanFactors = response.data?.loanFactors?.[0];

            if (invoiceData && loanFactors) {
              // Extract values from response
              const totalAmount = parseFloat(invoiceData.totalAmount) || parseFloat(invoiceDetails.totalAmount) || 0;
              const maxROI = loanFactors.maxROI || 12;
              const approvedLoanAmountNum = totalAmount * 0.8; // 80% of invoice amount
              const expectedDate = getLastDayOfMonthYYYYMMDD(); // yyyy-mm-dd format for date input
              const expectedDateForCalc = getLastDayOfMonth(); // dd-mm-yy format for calculation
              
              // Calculate duration from today to expected payment date
              const today = new Date();
              const todayStr = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getFullYear()).slice(-2)}`;
              const durationDays = calculateDaysBetween(todayStr, expectedDateForCalc);

              setLoanOffer(prev => ({
                ...prev,
                invoiceAmount: formatCurrency(totalAmount),
                approvedLoanAmount: formatCurrency(approvedLoanAmountNum),
                requiredLoanAmount: formatCurrency(approvedLoanAmountNum), // Initially same as approved
                rateOfInterest: `${maxROI}%`,
                expectedPaymentDate: expectedDate,
                durationOfLoan: `${durationDays} days`
              }));

              // Extract and decrypt contactMobile and contactEmail from invoiceData if available
              if (invoiceData.contactMobile || invoiceData.contactEmail || invoiceData.contactPersonName) {
                try {
                  let decryptedMobile = invoiceData.contactMobile;
                  let decryptedEmail = invoiceData.contactEmail;

                  // Decrypt mobile number if it exists and appears to be encrypted
                  if (invoiceData.contactMobile) {
                    try {
                      console.log("Decrypting contactMobile from lookup API response");
                      decryptedMobile = await decrypt(invoiceData.contactMobile.trim());
                      console.log("Decrypted mobile:", decryptedMobile);
                    } catch (decryptError) {
                      console.warn("Failed to decrypt contactMobile, using as-is:", decryptError);
                      // If decryption fails, check if it's already a plain number
                      const plainNumber = invoiceData.contactMobile.replace(/\D/g, '');
                      if (plainNumber.length === 10) {
                        decryptedMobile = plainNumber;
                      }
                    }
                  }

                  // Decrypt email if it exists and appears to be encrypted
                  if (invoiceData.contactEmail) {
                    try {
                      console.log("Decrypting contactEmail from lookup API response");
                      decryptedEmail = await decrypt(invoiceData.contactEmail.trim());
                      console.log("Decrypted email:", decryptedEmail);
                    } catch (decryptError) {
                      console.warn("Failed to decrypt contactEmail, using as-is:", decryptError);
                      // If decryption fails, check if it looks like a valid email
                      if (invoiceData.contactEmail.includes('@')) {
                        decryptedEmail = invoiceData.contactEmail;
                      }
                    }
                  }

                  // Update company details with decrypted contact information
                  setCompanyDetails(prev => ({
                    ...prev,
                    contactName: invoiceData.contactPersonName || prev.contactName || "",
                    contactEmail: decryptedEmail || prev.contactEmail || "",
                    contactMobile: decryptedMobile || prev.contactMobile || ""
                  }));

                  console.log("Company contact details updated from invoice lookup API");
                } catch (error) {
                  console.error("Error processing contact details from invoice lookup:", error);
                  // Don't block the flow if contact details processing fails
                }
              }
            }
          }
        } catch (error) {
          console.error("Error fetching invoice lookup:", error);
          // Fallback to using invoiceDetails if API fails
          const invoiceAmountNum = parseFloat(invoiceDetails.totalAmount) || 0;
          const approvedLoanAmountNum = invoiceAmountNum * 0.8;
          const expectedDate = getLastDayOfMonthYYYYMMDD(); // yyyy-mm-dd format for date input
          const expectedDateForCalc = getLastDayOfMonth(); // dd-mm-yy format for calculation
          
          const today = new Date();
          const todayStr = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getFullYear()).slice(-2)}`;
          const durationDays = calculateDaysBetween(todayStr, expectedDateForCalc);

          setLoanOffer(prev => ({
            ...prev,
            invoiceAmount: formatCurrency(invoiceAmountNum),
            approvedLoanAmount: formatCurrency(approvedLoanAmountNum),
            requiredLoanAmount: formatCurrency(approvedLoanAmountNum),
            expectedPaymentDate: expectedDate,
            durationOfLoan: `${durationDays} days`
          }));
        }
      };

      fetchInvoiceLookup();
    }
  }, [step, invoiceDetails.invoiceNumber]);

  // Calculate interest and total amount when required loan amount, rate, or duration changes
  useEffect(() => {
    if (step === 3 && loanOffer.requiredLoanAmount && loanOffer.rateOfInterest && loanOffer.durationOfLoan) {
      const calculateInterest = () => {
        try {
          // Extract numeric values
          let requiredLoanAmount = extractNumericValue(loanOffer.requiredLoanAmount);
          
          // If amount seems too small, try extracting from approved amount as fallback
          if (requiredLoanAmount < 100) {
            console.warn("Required loan amount seems too small, checking approved amount:", requiredLoanAmount);
            const approvedAmount = extractNumericValue(loanOffer.approvedLoanAmount);
            if (approvedAmount > requiredLoanAmount) {
              requiredLoanAmount = approvedAmount;
              console.log("Using approved amount:", requiredLoanAmount);
            }
          }
          
          // Extract annual rate from "12%" format (maxROI)
          const rateStr = loanOffer.rateOfInterest.replace(/[^\d.]/g, '');
          const maxROI = parseFloat(rateStr) || 0;
          
          // Extract days from "20 days" format
          const daysStr = loanOffer.durationOfLoan.replace(/[^\d]/g, '');
          const days = parseInt(daysStr) || 0;

          // Debug logging
          console.log("Interest Calculation - Input values:", {
            requiredLoanAmount: loanOffer.requiredLoanAmount,
            extractedRequiredLoanAmount: requiredLoanAmount,
            rateOfInterest: loanOffer.rateOfInterest,
            extractedMaxROI: maxROI,
            durationOfLoan: loanOffer.durationOfLoan,
            extractedDays: days
          });

          // Calculate using formula: interest = (requiredLoanAmount * maxROI * days) / (365 * 100)
          if (requiredLoanAmount > 0 && maxROI > 0 && days > 0) {
            const interest = (requiredLoanAmount * maxROI * days) / (365 * 100);
            const totalAmount = requiredLoanAmount + interest;
            
            console.log("Interest Calculation - Results:", {
              interest: interest,
              totalAmount: totalAmount
            });
            
            setLoanOffer(prev => ({
              ...prev,
              interestToBePaid: formatToIndianRupee(interest),
              totalAmountToBePaid: formatToIndianRupee(totalAmount)
            }));
          }
        } catch (error) {
          console.error("Error calculating interest:", error);
          // Don't show error to user, just log it
        }
      };

      // Debounce the calculation to avoid too many updates
      const timeoutId = setTimeout(() => {
        calculateInterest();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [step, loanOffer.requiredLoanAmount, loanOffer.rateOfInterest, loanOffer.durationOfLoan]);

  // Handle required loan amount change
  const handleRequiredLoanAmountChange = (e) => {
    const value = e.target.value;
    setRequiredLoanAmountError("");
    
    // Remove any existing currency symbols, spaces, and commas for processing
    let cleanValue = value.replace(/[₹Rs,\s]/gi, '');
    
    // If user is typing and value doesn't contain "₹", add it
    if (cleanValue && !value.includes('₹')) {
      // Only allow numbers and decimal point
      cleanValue = cleanValue.replace(/[^\d.]/g, '');
      // Ensure only one decimal point
      const parts = cleanValue.split('.');
      if (parts.length > 2) {
        cleanValue = parts[0] + '.' + parts.slice(1).join('');
      }
      // Limit to 2 decimal places while typing
      if (parts.length === 2 && parts[1].length > 2) {
        cleanValue = parts[0] + '.' + parts[1].substring(0, 2);
      }
      const newValue = cleanValue ? `₹ ${cleanValue}` : '';
      setLoanOffer(prev => ({
        ...prev,
        requiredLoanAmount: newValue
      }));
    } else {
      setLoanOffer(prev => ({
        ...prev,
        requiredLoanAmount: value
      }));
    }
  };

  // Format required loan amount on blur
  const handleRequiredLoanAmountBlur = (e) => {
    const value = e.target.value.trim();
    
    // Extract numeric value for validation and formatting (remove ₹, Rs, commas, spaces)
    const numericValue = parseFloat(value.replace(/[₹Rs,\s]/gi, '')) || 0;
    const approvedAmount = extractNumericValue(loanOffer.approvedLoanAmount);
    const minimumAmount = 100;
    
    // If user entered something (not empty and not just currency symbol)
    if (value !== "" && value !== '₹' && value.toLowerCase() !== 'rs') {
      if (numericValue > 0) {
        // Validate minimum amount
        if (numericValue < minimumAmount) {
          setRequiredLoanAmountError(`Required loan amount must be at least ₹ ${minimumAmount.toLocaleString('en-IN')}`);
          // Set to minimum amount with 2 decimal places
          setLoanOffer(prev => ({
            ...prev,
            requiredLoanAmount: formatToIndianRupee(minimumAmount)
          }));
        }
        // Validate against approved amount
        else if (numericValue > approvedAmount) {
          setRequiredLoanAmountError(`Required loan amount cannot exceed approved loan amount (${loanOffer.approvedLoanAmount})`);
          // Clamp to approved amount
          setLoanOffer(prev => ({
            ...prev,
            requiredLoanAmount: prev.approvedLoanAmount
          }));
        } else {
          // Format the value properly with 2 decimal places
          setLoanOffer(prev => ({
            ...prev,
            requiredLoanAmount: formatToIndianRupee(numericValue)
          }));
          setRequiredLoanAmountError("");
        }
      } else {
        // Invalid input, reset to approved amount
        setRequiredLoanAmountError("Please enter a valid amount");
        setLoanOffer(prev => ({
          ...prev,
          requiredLoanAmount: prev.approvedLoanAmount
        }));
      }
    } else {
      // If empty or only currency symbol, set to approved amount
      setLoanOffer(prev => ({
        ...prev,
        requiredLoanAmount: prev.approvedLoanAmount
      }));
      setRequiredLoanAmountError("");
    }
  };

  // Handle expected payment date change (from DatePicker)
  const handleExpectedPaymentDateChange = (date) => {
    if (!date || !date.isValid()) return;
    
    const dateString = dayjsToString(date);
    
    // Update the expected payment date
    setLoanOffer(prev => ({
      ...prev,
      expectedPaymentDate: dateString
    }));
    
    // Recalculate duration
    const today = new Date();
    const todayStr = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getFullYear()).slice(-2)}`;
    
    // Convert yyyy-mm-dd to dd-mm-yy format for calculateDaysBetween
    const dateParts = dateString.split('-');
    const expectedDateStr = `${dateParts[2]}-${dateParts[1]}-${dateParts[0].slice(-2)}`;
    
    const durationDays = calculateDaysBetween(todayStr, expectedDateStr);
    
    // Update duration of loan
    setLoanOffer(prev => ({
      ...prev,
      expectedPaymentDate: dateString,
      durationOfLoan: `${durationDays} days`
    }));
  };

  // TODO: Replace with actual data passed from hero section (e.g., via location.state or context)
  // For now, keep initialInvoiceDetails as placeholder

  // Editable fields for verification
  const validationRules = {
    sellerGST: {
      pattern: /^([0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1})$/, // 15-digit GSTIN
      hint: "15-digit GSTIN • e.g., 22AAAAA0000A1Z5"
    },
    sellerPAN: {
      pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
      hint: "10-digit PAN • e.g., ABCDE1234F"
    },
    buyerGST: {
      pattern: /^([0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1})$/, // 15-digit GSTIN
      hint: "15-digit GSTIN • e.g., 22AAAAA0000A1Z5"
    },
    buyerPAN: {
      pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
      hint: "10-digit PAN • e.g., ABCDE1234F"
    },
    invoiceNumber: {
      minLength: 2,
      maxLength: 30,
      hint: "Invoice number (2-30 characters)"
    },
    invoiceDate: {
      pattern: /^\d{4}-\d{2}-\d{2}$/,
      hint: "Date in YYYY-MM-DD format"
    },
    dueDate: {
      pattern: /^\d{4}-\d{2}-\d{2}$/,
      hint: "Date in YYYY-MM-DD format (optional)"
    },
    totalAmount: {
      pattern: /^\d+(\.\d{1,2})?$/,
      hint: "Amount in numbers (e.g., 12345.67)"
    },
    cgst: {
      pattern: /^\d+(\.\d{1,2})?$/,
      hint: "Amount in numbers (e.g., 18.00)"
    },
    igst: {
      pattern: /^\d+(\.\d{1,2})?$/,
      hint: "Amount in numbers (e.g., 0.00)"
    },
    sgst: {
      pattern: /^\d+(\.\d{1,2})?$/,
      hint: "Amount in numbers (e.g., 18.00)"
    }
  };
  
  const requiredFields = [
    "sellerGST",
    "sellerPAN",
    "buyerGST",
    "buyerPAN",
    "invoiceNumber",
    "invoiceDate",
    "totalAmount"
  ];
  
  function validateField(name, value) {
    const rule = validationRules[name];
    if (requiredFields.includes(name) && (!value || value.toString().trim() === "")) {
      return "This field is required";
    }
    if (rule) {
      if (rule.minLength && value && value.length < rule.minLength) {
        return `Minimum ${rule.minLength} characters required`;
      }
      if (rule.maxLength && value && value.length > rule.maxLength) {
        return `Maximum ${rule.maxLength} characters allowed`;
      }
      if (rule.pattern && value) {
        // For amount fields, remove currency symbols, Rs, and commas before validation
        let testValue = value;
        if (["totalAmount", "cgst", "sgst", "igst"].includes(name)) {
          testValue = value.replace(/[₹Rs,\s]/gi, '');
        }
        if (!rule.pattern.test(testValue)) {
        return "Invalid format";
        }
      }
    }
    return "";
  }
  
  // Remove this line:
  // const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [focusedFields, setFocusedFields] = useState({});
  const isLabelClick = useRef(false);

  // Helper to check if all required fields are filled and valid
  const allRequiredFieldsValid = requiredFields.every(
    (field) => invoiceDetails[field] && !errors[field]
  );
  
  // Format amount fields with ₹ symbol when component loads
  useEffect(() => {
    const amountFields = ["totalAmount", "cgst", "sgst", "igst"];
    let needsUpdate = false;
    const updatedDetails = { ...invoiceDetails };
    
    amountFields.forEach((field) => {
      const value = invoiceDetails[field];
      if (value && typeof value === 'string' && value.trim() !== '') {
        // Check if value is already properly formatted with ₹ and space
        const isProperlyFormatted = value.includes('₹ ');
        if (!isProperlyFormatted) {
          // Try to extract numeric value and format it
          const numericValue = extractNumericValue(value);
          if (numericValue > 0) {
            updatedDetails[field] = formatToIndianRupee(numericValue);
            needsUpdate = true;
          }
        }
      }
    });
    
    if (needsUpdate) {
      setInvoiceDetails(updatedDetails);
    }
  }, []); // Run only on mount
  
  // Validate all fields on mount if initial data is present
  useEffect(() => {
    const newErrors = {};
    Object.keys(invoiceDetails).forEach((field) => {
      const error = validateField(field, invoiceDetails[field]);
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);
    // Mark all fields as touched so errors show
    const allTouched = {};
    Object.keys(invoiceDetails).forEach((field) => {
      allTouched[field] = true;
    });
    setTouched(allTouched);
  }, [invoiceDetails]);
  
  const handleDetailChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    
    // Auto-uppercase for GST and PAN fields
    if (["sellerGST", "buyerGST", "sellerPAN", "buyerPAN"].includes(name)) {
      newValue = newValue.toUpperCase();
    }
    
    // For amount fields, add "₹ " prefix when user starts typing
    if (["totalAmount", "cgst", "sgst", "igst"].includes(name)) {
      // Remove any existing currency symbols, spaces, and commas for processing
      let cleanValue = newValue.replace(/[₹Rs,\s]/gi, '');
      
      // If user is typing and value doesn't start with "₹", add it
      if (cleanValue && !newValue.includes('₹')) {
        // Only allow numbers and decimal point
        cleanValue = cleanValue.replace(/[^\d.]/g, '');
        // Ensure only one decimal point
        const parts = cleanValue.split('.');
        if (parts.length > 2) {
          cleanValue = parts[0] + '.' + parts.slice(1).join('');
        }
        // Limit to 2 decimal places while typing
        if (parts.length === 2 && parts[1].length > 2) {
          cleanValue = parts[0] + '.' + parts[1].substring(0, 2);
        }
        newValue = cleanValue ? `₹ ${cleanValue}` : '';
      } else if (!cleanValue) {
        newValue = '';
      }
    }
    
    setInvoiceDetails((prev) => ({ ...prev, [name]: newValue }));
    // Real-time validation
    const error = validateField(name, newValue);
    setErrors((prev) => ({ ...prev, [name]: error }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  // Handle invoice date change (from DatePicker)
  const handleInvoiceDateChange = (date) => {
    const dateString = dayjsToString(date);
    setInvoiceDetails((prev) => ({ ...prev, invoiceDate: dateString }));
    // Real-time validation
    const error = validateField('invoiceDate', dateString);
    setErrors((prev) => ({ ...prev, invoiceDate: error }));
    setTouched((prev) => ({ ...prev, invoiceDate: true }));
  };

  // Handle field focus
  const handleFieldFocus = (fieldName) => {
    setFocusedFields((prev) => ({ ...prev, [fieldName]: true }));
  };

  // Handle field blur
  const handleFieldBlur = (fieldName, e) => {
    // Always hide the hint when field loses focus
    setFocusedFields((prev) => {
      const newState = { ...prev };
      delete newState[fieldName];
      return newState;
    });
  };
  
  // Format amount fields on blur
  const handleAmountBlur = (e) => {
    const { name, value } = e.target;
    if (["totalAmount", "cgst", "sgst", "igst"].includes(name)) {
      // Extract numeric value (remove ₹, Rs, commas, spaces)
      const numericValue = parseFloat(value.replace(/[₹Rs,\s]/gi, '')) || 0;
      if (numericValue > 0) {
        // Format with 2 decimal places
        const formattedValue = formatToIndianRupee(numericValue);
        setInvoiceDetails((prev) => ({ ...prev, [name]: formattedValue }));
      } else if (value.trim() === '' || value.trim() === '₹' || value.trim().toLowerCase() === 'rs') {
        // Clear field if empty or only currency symbol
        setInvoiceDetails((prev) => ({ ...prev, [name]: '' }));
      }
    }
  };

  // Step 1: Confirm details
  const handleConfirmDetails = async () => {
    if (!consent1) {
      setErrorMessage("Please provide consent to proceed.");
      return;
    }
    
    // Validate required fields
    if (!allRequiredFieldsValid) {
      setErrorMessage("Please fill all required fields correctly.");
      return;
    }

    setErrorMessage("");
    setIsProcessing(true);

    try {
      // Get documentId from location.state or localStorage
      const documentId = getDocumentId();
      
      if (!documentId) {
        throw new Error("Document ID not found. Please upload the invoice again.");
      }

      // Prepare API payload
      const payload = {
        documentId: documentId,
        sellerGstNumber: invoiceDetails.sellerGST,
        sellerPanNumber: invoiceDetails.sellerPAN,
        buyerGstNumber: invoiceDetails.buyerGST,
        buyerPanNumber: invoiceDetails.buyerPAN,
        consentGiven: consent1,
        consentText: "I allow GrowBizh to fetch my company & director details based on my company GST number.",
        totalAmount: extractNumericValue(invoiceDetails.totalAmount) || 0,
        cgstAmount: extractNumericValue(invoiceDetails.cgst) || 0,
        sgstAmount: extractNumericValue(invoiceDetails.sgst) || 0,
        igstAmount: extractNumericValue(invoiceDetails.igst) || 0,
        expectedPaymentDate: invoiceDetails.dueDate || ""
      };

      // Make API call
      const response = await axios.post(
        `${CREDIT_API_URL}invoices/consent-and-verify`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200 || response.data?.status === "Success") {
        // Check for anchorPresenceFlag
        const anchorPresenceFlag = response.data?.data?.anchorPresenceFlag;
        
        // Only proceed if anchor is verified (flag === 2)
        if (anchorPresenceFlag !== ANCHOR_PRESENCE_FLAG.VERIFIED) {
          // Check if PENDING or REJECTED - show notification popup
          if (anchorPresenceFlag === ANCHOR_PRESENCE_FLAG.PENDING) {
            setShowPendingNotification(true);
            setIsProcessing(false);
            return;
          } else if (anchorPresenceFlag === ANCHOR_PRESENCE_FLAG.REJECTED) {
            setShowRejectedNotification(true);
            setIsProcessing(false);
            return;
          }
          
          // NOT_PRESENT - show form popup to register buyer
          // Pre-fill buyer GST and PAN from invoice details
          setBuyerDetails(prev => ({
            ...prev,
            buyerGst: invoiceDetails.buyerGST || "",
            buyerPan: invoiceDetails.buyerPAN || ""
          }));
          setAnchorPresenceStatus(anchorPresenceFlag);
          setAnchorStatusMessage(getAnchorStatusMessage(anchorPresenceFlag));
          setShowAnchorNotFoundPopup(true);
          setIsProcessing(false);
          return; // Don't proceed to step 2
        }
        
        // anchorPresenceFlag === 2 (VERIFIED) - Continue with existing flow
        // Call GST verification API to fetch company details
        try {
          const gstVerifyResponse = await axios.post(
            `${CREDIT_API_URL}gst/verify`,
            {
              gstNumber: invoiceDetails.sellerGST,
              documentId: documentId
            },
            {
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );

          if (gstVerifyResponse.status === 200 && gstVerifyResponse.data?.status === "Success") {
            const verificationResult = gstVerifyResponse.data?.data?.verificationResult;
            
            if (verificationResult) {
              // Map GST verification response to company details
              const address = verificationResult.address || {};
              const addressLine = [address.addressLine1, address.addressLine2]
                .filter(Boolean)
                .join(", ");
              
              // Convert industryType array to string
              const natureOfWork = verificationResult.industryType 
                ? verificationResult.industryType.join(" & ")
                : "";

              // Convert GST status
              const gstActive = address.gstinStatus === "Active" ? "Yes" : address.gstinStatus || "No";

              // Get first director name for contact name
              const contactName = verificationResult.directorNames && verificationResult.directorNames.length > 0
                ? verificationResult.directorNames[0].trim()
                : "";

              // Update company details with fetched data, reset other fields to empty
              setCompanyDetails({
                businessType: verificationResult.businessType || "",
                legalName: verificationResult.nameOfOrgAsPerGST || "",
                registeredAddress: addressLine || "",
                natureOfWork: natureOfWork || "",
                annualTurnoverSlab: verificationResult.annual_turnover || "",
                panNumber: "", // Keep empty for manual entry
                gstActive: gstActive,
                contactName: contactName, // First director name
                contactEmail: verificationResult.email || "",
                contactMobile: verificationResult.mobile || ""
              });
            }
          }
        } catch (gstError) {
          console.error("Error in GST verification:", gstError);
          // Don't block the flow if GST verification fails, just log the error
          // User can still proceed and manually fill the details
        }

        // Store response data if needed for next steps
        if (response.data?.data) {
          // You can store company details from response if provided
          setCompanyDetails(prev => ({
            ...prev,
            ...response.data.data
          }));
        }
    setStep(2);
      } else {
        throw new Error(response.data?.message || "Verification failed");
      }
    } catch (error) {
      console.error("Error in consent and verify:", error);
      setErrorMessage(
        error.response?.data?.message || 
        error.message || 
        "Failed to verify invoice details. Please try again."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Step 2: Handle company details change
  const handleCompanyDetailChange = (e) => {
    const { name, value } = e.target;
    setCompanyDetails((prev) => ({ ...prev, [name]: value }));
  };

  // Handle company detail field focus
  const handleCompanyFieldFocus = (fieldName) => {
    setFocusedFields((prev) => ({ ...prev, [fieldName]: true }));
  };

  // Handle company detail field blur
  const handleCompanyFieldBlur = (fieldName) => {
    setFocusedFields((prev) => {
      const newState = { ...prev };
      delete newState[fieldName];
      return newState;
    });
  };

  // Step 2: Verify company details
  const handleVerifyCompanyDetails = async () => {
    if (!companyConsent) {
      setOtpError("Please confirm that the details are correct");
      return;
    }

    setOtpError("");
    setErrorMessage("");
    setIsProcessingStep2(true);

    try {
      // Get documentId from location.state or localStorage
      const documentId = getDocumentId();
      
      if (!documentId) {
        throw new Error("Document ID not found. Please start from the beginning.");
      }

      // Create lead first before consent-and-verify API call
      let leadIdValue = null;
      try {
        // Extract mobile number (remove any non-digit characters)
        const mobileNumber = companyDetails.contactMobile ? 
          parseInt(companyDetails.contactMobile.replace(/\D/g, '')) : null;

        if (!mobileNumber || mobileNumber.toString().length !== 10) {
          console.warn("Invalid mobile number for lead creation:", companyDetails.contactMobile);
        }

        const leadPayload = {
          mobile: mobileNumber,
          email: companyDetails.contactEmail || "",
          name: companyDetails.contactName || "",
          productType: "Supply Chain Financing",
          isConsentAccepted: true,
        };
        // Attach client/browser info
        try {
          const clientInfo = getClientInfo();
          leadPayload.browserName = clientInfo.browserName || null;
          leadPayload.deviceType = clientInfo.deviceType || null;
        } catch (e) {
          // ignore detection errors
        } 
        // Include UTM tracking params if present
        if (utm) {
          leadPayload.utmSource = utm.utmSource || null;
          leadPayload.utmMedium = utm.utmMedium || null;
          leadPayload.utmCampaign = utm.utmCampaign || "GrowBizh";
        } 

        const leadResponse = await axios.post(
          `${CREDIT_API_URL}create-lead`,
          leadPayload,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        ).catch(error => {
          // Handle 400 status with mobile not verified message
          if (error.response?.status === 400 && 
              error.response?.data?.message?.includes("Mobile is not verified")) {
            // Return the error response as if it were successful for leadId extraction
            return error.response;
          }
          // Handle 409 status with lead already exists message
          if (error.response?.status === 409 && 
              error.response?.data?.message?.includes("Lead already exist")) {
            // Return the error response as if it were successful for leadId extraction
            return error.response;
          }
          throw error;
        });

        // Handle 200 (success), 400 (mobile not verified), and 409 (lead already exists) responses
        if (leadResponse.status === 200 || 
            (leadResponse.status === 400 && 
             leadResponse.data?.message?.includes("Mobile is not verified")) ||
            (leadResponse.status === 409 && 
             leadResponse.data?.message?.includes("Lead already exist"))) {
          
          if (leadResponse.status === 200) {
            console.log("Lead created successfully:", leadResponse.data);
          } else if (leadResponse.status === 400) {
            console.log("Existing lead found (mobile not verified):", leadResponse.data);
          } else if (leadResponse.status === 409) {
            console.log("Lead already exists (Conflict):", leadResponse.data);
          }
          
          // Extract leadId from response (works for success, mobile not verified, and conflict cases)
          if (leadResponse.data?.data?.id) {
            leadIdValue = leadResponse.data.data.id;
          } else if (leadResponse.data?.data?._id) {
            leadIdValue = leadResponse.data.data._id;
          } else if (leadResponse.data?.id) {
            leadIdValue = leadResponse.data.id;
          }
          
          // Store leadId in state
          if (leadIdValue) {
            setLeadId(leadIdValue);
          }
        } else {
          console.warn("Lead creation returned unexpected status:", leadResponse.status);
        }
      } catch (leadError) {
        console.error("Error creating lead:", leadError);
        throw new Error("Failed to create lead. Please try again.");
      }

      // Convert natureOfWork to array format
      const natureBusActivities = companyDetails.natureOfWork 
        ? companyDetails.natureOfWork.split('&').map(item => item.trim()).filter(item => item)
        : [];

      // Convert GST status
      const gstStatus = companyDetails.gstActive === "Yes" ? "Active" : companyDetails.gstActive;

      // Prepare API payload with leadId
      const payload = {
        documentId: documentId,
        leadId: leadIdValue,
        sellerPanNumber: companyDetails.panNumber,
        consentGiven: companyConsent,
        consentText: "Please check the data properly. It will help in preparing best offer for you.",
        businessName: companyDetails.legalName,
        legalName: companyDetails.legalName,
        businessType: companyDetails.businessType,
        gstStatus: gstStatus,
        annualTurnover: companyDetails.annualTurnoverSlab,
        address: companyDetails.registeredAddress,
        contactPersonName: companyDetails.contactName,
        contactEmail: companyDetails.contactEmail,
        contactMobile: companyDetails.contactMobile,
        natureBusActivities: natureBusActivities
      };

      // Make API call with leadId
      const response = await axios.post(
        `${CREDIT_API_URL}invoices/consent-and-verify`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200 || response.data?.status === "Success") {
        // Store response data if needed for next steps
        if (response.data?.data) {
          // Update loan offer details from response if provided
          setLoanOffer(prev => ({
            ...prev,
            ...response.data.data
          }));
        }

        setStep(3);
      } else {
        throw new Error(response.data?.message || "Verification failed");
      }
    } catch (error) {
      console.error("Error in company details verification:", error);
      setOtpError(
        error.response?.data?.message || 
        error.message || 
        "Failed to verify company details. Please try again."
      );
    } finally {
      setIsProcessingStep2(false);
    }
  };

  // Step 3: Complete application
  // Convert yyyy-mm-dd to dd-mm-yy format
  const convertDateToDDMMYY = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0].slice(-2)}`;
    }
    return dateStr;
  };

  const handleCompleteApplication = async () => {
    if (!loanOfferConsent) {
      setErrorMessage("Please provide consent to proceed.");
      return;
    }

    if (!leadId) {
      setErrorMessage("Lead ID not found. Please go back and complete Step 2.");
      return;
    }

    setErrorMessage("");
    setIsProcessingStep3(true);

    try {
      // Get documentId from location.state or localStorage
      const documentId = getDocumentId();
      
      if (!documentId) {
        throw new Error("Document ID not found. Please start from the beginning.");
      }

      // Extract numeric values from formatted strings
      const requiredLoanAmount = extractNumericValue(loanOffer.requiredLoanAmount);
      const approvedLoanAmount = extractNumericValue(loanOffer.approvedLoanAmount);
      const rateOfInterest = parseFloat(loanOffer.rateOfInterest.replace(/[^\d.]/g, '')) || 0;
      const durationOfLoan = parseInt(loanOffer.durationOfLoan.replace(/[^\d]/g, '')) || 0;
      const interestToBePaid = extractNumericValue(loanOffer.interestToBePaid);
      const totalAmountToBePaid = extractNumericValue(loanOffer.totalAmountToBePaid);
      
      // Convert expectedPaymentDate from yyyy-mm-dd to dd-mm-yy
      const expectedPaymentDate = convertDateToDDMMYY(loanOffer.expectedPaymentDate);

      // Prepare API payload
      const payload = {
        documentId: documentId,
        leadId: leadId,
        consentGiven: true,
        consentText: "I confirm that the details are correct as per my knowledge.",
        requiredLoanAmount: Math.round(requiredLoanAmount),
        approvedLoanAmount: Math.round(approvedLoanAmount),
        rateOfInterest: rateOfInterest,
        durationOfLoan: durationOfLoan,
        durationOfLoanType: "days",
        interestToBePaid: parseFloat(interestToBePaid.toFixed(2)),
        totalAmountToBePaid: parseFloat(totalAmountToBePaid.toFixed(2)),
        expectedPaymentDate: expectedPaymentDate
      };

      console.log("Submitting application with payload:", payload);

      // Make API call
      const response = await axios.post(
        `${CREDIT_API_URL}invoices/consent-and-verify`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200 || response.data?.status === "Success") {
        // Generate OTP - decrypt mobile number first
        let mobileNumber = null;
        
        // Check if contactMobile exists and is not empty
        if (!companyDetails.contactMobile || companyDetails.contactMobile.trim() === "") {
          console.error("Contact mobile number is missing or empty");
          setErrorMessage("Mobile number is missing. Please check your contact details.");
          setIsProcessingStep3(false);
          return;
        }
        
        try {
          let decryptedMobile = companyDetails.contactMobile;
          
          // Always try to decrypt first (mobile number should be encrypted)
          try {
            console.log("Attempting to decrypt mobile number. Length:", companyDetails.contactMobile.length);
            console.log("First 50 chars:", companyDetails.contactMobile.substring(0, 50));
            decryptedMobile = await decrypt(companyDetails.contactMobile.trim());
            console.log("Decrypted mobile number:", decryptedMobile);
          } catch (decryptError) {
            console.warn("Decryption failed, checking if already decrypted:", decryptError);
            // If decryption fails, check if it's already a plain number
            const plainNumber = companyDetails.contactMobile.replace(/\D/g, '');
            if (plainNumber.length === 10) {
              // Already a plain 10-digit number, use as-is
              console.log("Mobile number appears to be already decrypted");
              decryptedMobile = plainNumber;
            } else {
              // Re-throw the error if it's not a plain number
              throw new Error(`Decryption failed and value is not a valid 10-digit number. Value: ${companyDetails.contactMobile.substring(0, 50)}`);
            }
          }
          
          // Extract numeric value from decrypted mobile (remove any non-digit characters)
          const numericMobile = decryptedMobile.replace(/\D/g, '');
          mobileNumber = parseInt(numericMobile);
          
          console.log("Final mobile number for OTP:", mobileNumber, "Length:", mobileNumber?.toString().length);
          
          // Validate the extracted number
          if (!mobileNumber || isNaN(mobileNumber) || mobileNumber.toString().length !== 10) {
            throw new Error(`Invalid mobile number after decryption. Got: ${mobileNumber}, Length: ${mobileNumber?.toString().length || 0}`);
          }
        } catch (error) {
          console.error("Error processing mobile number:", error);
          console.error("Original mobile value:", companyDetails.contactMobile);
          console.error("Company details:", companyDetails);
          // Fallback: try to extract number directly
          const fallbackNumber = companyDetails.contactMobile.replace(/\D/g, '');
          if (fallbackNumber.length === 10) {
            mobileNumber = parseInt(fallbackNumber);
            console.log("Using fallback mobile number:", mobileNumber);
          } else {
            console.error("Fallback also failed. Extracted:", fallbackNumber, "Length:", fallbackNumber.length);
            setErrorMessage(`Failed to process mobile number: ${error.message}`);
            setIsProcessingStep3(false);
            return;
          }
        }

        if (mobileNumber && mobileNumber.toString().length === 10) {
          try {
            const otpResponse = await axios.post(
              `${CREDIT_API_URL}generate-otp`,
              {
                mobile: mobileNumber,
                productType: "Supply Chain Financing"
              },
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              }
            );

            if (otpResponse.status === 200) {
              // Store decrypted mobile number for OTP modal
              setDecryptedMobileNumber(mobileNumber);
              // Show OTP modal with decrypted mobile number
              setShowOtpModal(true);
            } else {
              throw new Error("OTP generation failed");
            }
          } catch (otpError) {
            console.error("Error generating OTP:", otpError);
            setErrorMessage("Failed to generate OTP. Please try again.");
          }
        } else {
          console.error("Mobile number validation failed:", {
            contactMobile: companyDetails.contactMobile,
            mobileNumber: mobileNumber,
            mobileNumberLength: mobileNumber ? mobileNumber.toString().length : 0
          });
          setErrorMessage(`Invalid mobile number. Please check your contact details. Mobile: ${companyDetails.contactMobile || 'Not provided'}`);
        }
      } else {
        throw new Error(response.data?.message || "Application submission failed");
      }
    } catch (error) {
      console.error("Error in complete application:", error);
      setErrorMessage(
        error.response?.data?.message || 
        error.message || 
        "Failed to submit application. Please try again."
      );
    } finally {
      setIsProcessingStep3(false);
    }
  };

  // Handle buyer details change in popup
  const handleBuyerDetailChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    
    // Auto-uppercase for GST and PAN fields
    if (name === "buyerGst" || name === "buyerPan") {
      newValue = newValue.toUpperCase();
    }
    
    // Only allow digits for mobile
    if (name === "contactMobile") {
      newValue = newValue.replace(/\D/g, '').slice(0, 10);
    }
    
    setBuyerDetails(prev => ({ ...prev, [name]: newValue }));
    
    // Clear error when user starts typing
    if (buyerDetailsErrors[name]) {
      setBuyerDetailsErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Validate buyer details
  const validateBuyerDetails = () => {
    const errors = {};
    
    // GST validation
    if (!buyerDetails.buyerGst) {
      errors.buyerGst = "Buyer GST is required";
    } else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(buyerDetails.buyerGst)) {
      errors.buyerGst = "Invalid GST format";
    }
    
    // PAN validation
    if (!buyerDetails.buyerPan) {
      errors.buyerPan = "Buyer PAN is required";
    } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(buyerDetails.buyerPan)) {
      errors.buyerPan = "Invalid PAN format";
    }
    
    // Contact person name validation
    if (!buyerDetails.contactPersonName) {
      errors.contactPersonName = "Contact person name is required";
    }
    
    // Mobile validation
    if (!buyerDetails.contactMobile) {
      errors.contactMobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(buyerDetails.contactMobile)) {
      errors.contactMobile = "Invalid mobile number";
    }
    
    // Company name validation
    if (!buyerDetails.companyName) {
      errors.companyName = "Company name is required";
    }
    
    setBuyerDetailsErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit buyer details
  const handleSubmitBuyerDetails = async () => {
    if (!validateBuyerDetails()) {
      return;
    }
    
    setIsSubmittingBuyerDetails(true);
    setErrorMessage("");
    
    try {
      // API call to register buyer as anchor
      const payload = {
        companyName: buyerDetails.companyName,
        gstNumber: buyerDetails.buyerGst,
        contactName: buyerDetails.contactPersonName,
        contactMobile: buyerDetails.contactMobile,
        isConsentAccepted: false,
        panNumber: buyerDetails.buyerPan
      };
      
      const response = await axios.post(
        `${CREDIT_API_URL}anchor/register`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200 || response.status === 201 || response.data?.status === "Success") {
        // Extract anchorId/leadId from response
        const anchorId = response.data?.data?.anchorId || response.data?.anchorId  || null;
        
        // Call trigger-notification API if anchorId is available
        if (anchorId) {
          try {
            const notificationPayload = {
              companyName: buyerDetails.companyName,
              mobile: buyerDetails.contactMobile,
              leadId: anchorId
            };
            
            await axios.post(
              `${CREDIT_API_URL}anchor/trigger-notification`,
              notificationPayload,
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              }
            );
            console.log("Anchor notification triggered successfully");
          } catch (notificationError) {
            console.error("Error triggering anchor notification:", notificationError);
            // Don't block the flow if notification fails, just log the error
          }
        }
        
        // Close popup
        setShowAnchorNotFoundPopup(false);
        // Reset buyer details
        setBuyerDetails({
          buyerGst: "",
          buyerPan: "",
          contactPersonName: "",
          contactMobile: "",
          companyName: ""
        });
        // Reset anchor status
        setAnchorPresenceStatus(null);
        setAnchorStatusMessage("");
        // Clear saved state
        clearStateFromStorage();
        // Show success notification popup
        setNotificationMessage(
          <>
            Thank you for sharing details.
            <br />
            Your buyer has been notified. We will update you once onboarding is completed.
          </>
        );
        setNotificationType("success");
        setShowNotification(true);
      } else {
        throw new Error(response.data?.message || "Failed to register anchor");
      }
    } catch (error) {
      console.error("Error registering anchor:", error);
      setErrorMessage(
        error.response?.data?.message || 
        error.message || 
        "Failed to register anchor. Please try again."
      );
    } finally {
      setIsSubmittingBuyerDetails(false);
    }
  };

  // Close anchor not found popup
  const handleCloseAnchorNotFoundPopup = () => {
    setShowAnchorNotFoundPopup(false);
    setBuyerDetails({
      buyerGst: "",
      buyerPan: "",
      contactPersonName: "",
      contactMobile: "",
      companyName: ""
    });
    setBuyerDetailsErrors({});
    setAnchorPresenceStatus(null);
    setAnchorStatusMessage("");
  };


  // Handle OTP verification success
  const handleOtpVerified = () => {
    setShowOtpModal(false);
    setShowThankYou(true);
    // Set submitted date
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    setSubmittedDate(`${day}/${month}/${year}`);
    // Clear saved state after successful submission
    clearStateFromStorage();
  };

  // Countdown timer for redirect
  useEffect(() => {
    if (showThankYou && redirectCountdown > 0) {
      const timer = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/', { replace: true });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [showThankYou, redirectCountdown, navigate]);

  // Save state to localStorage whenever relevant state changes
  useEffect(() => {
    const stateToSave = {
      step,
      invoiceDetails,
      companyDetails,
      loanOffer,
      consent1,
      companyConsent,
      loanOfferConsent,
      leadId
    };
    saveStateToStorage(stateToSave);
  }, [step, invoiceDetails, companyDetails, loanOffer, consent1, companyConsent, loanOfferConsent, leadId]);

  // Clear state when component unmounts if application is complete
  useEffect(() => {
    return () => {
      // Only clear if we're showing thank you message (application complete)
      if (showThankYou) {
        clearStateFromStorage();
      }
    };
  }, [showThankYou]);

  // Set page title based on current step
  const pageTitle = showThankYou 
    ? 'Supply Chain Finance Application Initiated - GrowBizh'
    : step === 1 
      ? 'Supply Chain Finance Application - Invoice Details - GrowBizh'
      : step === 2
        ? 'Supply Chain Finance Application - Business Details - GrowBizh'
        : step === 3
          ? 'Supply Chain Finance Application - Offer Details - GrowBizh'
          : 'Supply Chain Finance Application - GrowBizh';

  usePageTitle(pageTitle);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <HeaderWithoutLang/>
      {!showThankYou && (
    <section className="onboard-container" style={{ marginBottom: 40 }}>
     
         

          {/* Stepper UI */}
          <div className="onboard-progress" style={{ marginBottom: 32 }}>
            <div className={`progress-step ${step === 1 ? 'active' : ''}`}>Invoice Details</div>
            <div className={`progress-step ${step === 2 ? 'active' : ''}`}>Company Details</div>
            <div className={`progress-step ${step === 3 ? 'active' : ''}`}>Check Loan Offer</div>
          </div>

          {/* Step 1: Show extracted invoice details for verification */}
          {step === 1 && (
            <div className="business-form-section" style={{ width: "100%" }}>
              <h3 style={{ textAlign: "center", marginBottom: 16 }}>We have extracted details from the invoice. Please verify.</h3>
              <div className="onboard-step" style={{ maxWidth: 900, margin: "0 auto" }}>
                <div className="input-row" style={{  gap: '16px 24px', flexWrap: 'wrap', display: 'flex' }}>
                    <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                      <input 
                        type="text" 
                        name="sellerGST" 
                        value={invoiceDetails.sellerGST} 
                        onChange={handleDetailChange}
                        onFocus={() => handleFieldFocus('sellerGST')}
                        onBlur={(e) => { handleFieldBlur('sellerGST', e); handleAmountBlur(e); }}
                        className="floating-input" 
                        placeholder=" " 
                        maxLength={15} 
                      />
                      <label className="floating-label" htmlFor="sellerGST">Your Company's GST Number</label>
                      {focusedFields.sellerGST && (
                      <div className="field-hint-container">
                        <span className="field-hint">{validationRules.sellerGST.hint}</span>
                      </div>
                      )}
                      {touched.sellerGST && errors.sellerGST && (
                        <div className="field-error">{errors.sellerGST}</div>
                      )}
                    </div>
                    <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                      <input 
                        type="text" 
                        name="sellerPAN" 
                        value={invoiceDetails.sellerPAN} 
                        onChange={handleDetailChange}
                        onFocus={() => handleFieldFocus('sellerPAN')}
                        onBlur={(e) => handleFieldBlur('sellerPAN', e)}
                        className="floating-input" 
                        placeholder=" " 
                        maxLength={10} 
                      />
                      <label className="floating-label" htmlFor="sellerPAN">Your Company's PAN Number</label>
                      {focusedFields.sellerPAN && (
                      <div className="field-hint-container">
                        <span className="field-hint">{validationRules.sellerPAN.hint}</span>
                      </div>
                      )}
                      {touched.sellerPAN && errors.sellerPAN && (
                        <div className="field-error">{errors.sellerPAN}</div>
                      )}
                    </div>

                    <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                      <input 
                        type="text" 
                        name="buyerGST" 
                        value={invoiceDetails.buyerGST} 
                        onChange={handleDetailChange}
                        onFocus={() => handleFieldFocus('buyerGST')}
                        onBlur={(e) => handleFieldBlur('buyerGST', e)}
                        className="floating-input" 
                        placeholder=" " 
                        maxLength={15} 
                      />
                      <label className="floating-label" htmlFor="buyerGST">Your Buyer's GST Number</label>
                      {focusedFields.buyerGST && (
                      <div className="field-hint-container">
                        <span className="field-hint">{validationRules.buyerGST.hint}</span>
                      </div>
                      )}
                      {touched.buyerGST && errors.buyerGST && (
                        <div className="field-error">{errors.buyerGST}</div>
                      )}
                    </div>
                    <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                      <input 
                        type="text" 
                        name="buyerPAN" 
                        value={invoiceDetails.buyerPAN} 
                        onChange={handleDetailChange}
                        onFocus={() => handleFieldFocus('buyerPAN')}
                        onBlur={(e) => handleFieldBlur('buyerPAN', e)}
                        className="floating-input" 
                        placeholder=" " 
                        maxLength={10} 
                      />
                      <label className="floating-label" htmlFor="buyerPAN">Your Buyer's PAN Number</label>
                      {focusedFields.buyerPAN && (
                      <div className="field-hint-container">
                        <span className="field-hint">{validationRules.buyerPAN.hint}</span>
                      </div>
                      )}
                      {touched.buyerPAN && errors.buyerPAN && (
                        <div className="field-error">{errors.buyerPAN}</div>
                      )}
                    </div>

                    <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                      <input 
                        type="text" 
                        name="invoiceNumber" 
                        value={invoiceDetails.invoiceNumber} 
                        onChange={handleDetailChange}
                        onFocus={() => handleFieldFocus('invoiceNumber')}
                        onBlur={(e) => handleFieldBlur('invoiceNumber', e)}
                        className="floating-input" 
                        placeholder=" " 
                        maxLength={30} 
                      />
                      <label className="floating-label" htmlFor="invoiceNumber">Invoice Number</label>
                      {focusedFields.invoiceNumber && (
                      <div className="field-hint-container">
                        <span className="field-hint">{validationRules.invoiceNumber.hint}</span>
                      </div>
                      )}
                      {touched.invoiceNumber && errors.invoiceNumber && (
                        <div className="field-error">{errors.invoiceNumber}</div>
                      )}
                    </div>
                    <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                      <DatePicker
                        value={stringToDayjs(invoiceDetails.invoiceDate)}
                        onChange={handleInvoiceDateChange}
                        onOpen={() => handleFieldFocus('invoiceDate')}
                        onClose={() => handleFieldBlur('invoiceDate', null)}
                        format="YYYY-MM-DD"
                        slotProps={{
                          textField: {
                            className: "floating-input",
                            placeholder: " ",
                            onFocus: () => handleFieldFocus('invoiceDate'),
                            onBlur: (e) => handleFieldBlur('invoiceDate', e),
                            variant: "standard",
                            InputProps: {
                              disableUnderline: true,
                            },
                            inputProps: {
                              style: {
                                padding: '0',
                                height: '100%',
                                fontSize: '16px',
                              }
                            }
                          }
                        }}
                        sx={{ width: '100%' }}
                      />
                      <label className="floating-label" htmlFor="invoiceDate">Invoice Date</label>
                      {focusedFields.invoiceDate && (
                      <div className="field-hint-container">
                        <span className="field-hint">{validationRules.invoiceDate.hint}</span>
                      </div>
                      )}
                      {touched.invoiceDate && errors.invoiceDate && (
                        <div className="field-error">{errors.invoiceDate}</div>
                      )}
                    </div>
      
                    <div style={{ width: '100%', flexBasis: '100%' }}>
                      <h4 style={{margin:0, fontSize: 16, textDecoration: 'underline'}}>Invoice Amount Details</h4>
                    </div>

                    <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                      <input 
                        type="text" 
                        name="totalAmount" 
                        value={invoiceDetails.totalAmount} 
                        onChange={handleDetailChange}
                        onFocus={() => handleFieldFocus('totalAmount')}
                        onBlur={(e) => { handleFieldBlur('totalAmount', e); handleAmountBlur(e); }}
                        className="floating-input" 
                        placeholder=" " 
                      />
                      <label className="floating-label" htmlFor="totalAmount">Total Invoice Amount</label>
                      {focusedFields.totalAmount && (
                      <div className="field-hint-container">
                        <span className="field-hint">{validationRules.totalAmount.hint}</span>
                      </div>
                      )}
                      {touched.totalAmount && errors.totalAmount && (
                        <div className="field-error">{errors.totalAmount}</div>
                      )}
                    </div>
                    <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                      <input 
                        type="text" 
                        name="igst" 
                        value={invoiceDetails.igst} 
                        onChange={handleDetailChange}
                        onFocus={() => handleFieldFocus('igst')}
                        onBlur={(e) => { handleFieldBlur('igst', e); handleAmountBlur(e); }}
                        className="floating-input" 
                        placeholder=" " 
                      />
                      <label className="floating-label" htmlFor="igst">IGST</label>
                      {focusedFields.igst && (
                      <div className="field-hint-container">
                          <span className="field-hint">{validationRules.igst.hint}</span>
                      </div>
                      )}
                      {touched.igst && errors.igst && (
                        <div className="field-error">{errors.igst}</div>
                      )}
                    </div>
                    <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                      <input 
                        type="text" 
                        name="cgst" 
                        value={invoiceDetails.cgst} 
                        onChange={handleDetailChange}
                        onFocus={() => handleFieldFocus('cgst')}
                        onBlur={(e) => { handleFieldBlur('cgst', e); handleAmountBlur(e); }}
                        className="floating-input" 
                        placeholder=" " 
                      />
                      <label className="floating-label" htmlFor="cgst">CGST</label>
                      {focusedFields.cgst && (
                        <div className="field-hint-container">
                          <span className="field-hint">{validationRules.cgst.hint}</span>
                </div>
                      )}
                      {touched.cgst && errors.cgst && (
                        <div className="field-error">{errors.cgst}</div>
                      )}
                    </div>

                    <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                      <input 
                        type="text" 
                        name="sgst" 
                        value={invoiceDetails.sgst} 
                        onChange={handleDetailChange}
                        onFocus={() => handleFieldFocus('sgst')}
                        onBlur={(e) => { handleFieldBlur('sgst', e); handleAmountBlur(e); }}
                        className="floating-input" 
                        placeholder=" " 
                      />
                      <label className="floating-label" htmlFor="sgst">SGST</label>
                      {focusedFields.sgst && (
                        <div className="field-hint-container">
                          <span className="field-hint">{validationRules.sgst.hint}</span>
                        </div>
                      )}
                      {touched.sgst && errors.sgst && (
                        <div className="field-error">{errors.sgst}</div>
                      )}
                    </div>

                   
                     {/* Consent Checkbox */}
                  <div className="checkbox-container" style={{ width: '100%', flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <label 
              className="checkbox-item" 
              style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}
              onMouseDown={(e) => {
                isLabelClick.current = true;
                // Blur any focused input field
                if (document.activeElement && document.activeElement.tagName === 'INPUT') {
                  document.activeElement.blur();
                }
                // Toggle state immediately
                setConsent1(prev => !prev);
                e.preventDefault(); // Prevent default to avoid double toggle
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <input
                type="checkbox"
                checked={consent1}
                onChange={() => {
                  // Only toggle if not triggered by label click
                  if (!isLabelClick.current) {
                    setConsent1(prev => !prev);
                  } else {
                    // Reset flag after onChange
                    setTimeout(() => {
                      isLabelClick.current = false;
                    }, 0);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                        id="companyConsent"
               
              />
              <span>
                      I allow GrowBizh to fetch my company & director details based on my company GST number.
              </span>
            </label>
                  </div>

                    {/* <div className="floating-input-group">
                      <input type="text" name="dueDate" value={invoiceDetails.dueDate || ""} onChange={handleDetailChange} className="floating-input" placeholder=" " />
                      <label className="floating-label" htmlFor="dueDate">Expected Payment Date (Optional)</label>
                      <div className="field-hint-container">
                        <span className="field-hint">{validationRules.dueDate.hint}</span>
                      </div>
                      {touched.dueDate && errors.dueDate && (
                        <div className="field-error">{errors.dueDate}</div>
                      )}
                    </div> */}
                </div>
       
            
            
         
                {errorMessage && (
                  <div className="field-error" style={{ marginTop: 8 }}>{errorMessage}</div>
                )}
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <button
                      className="business-start-application-btn"
                      onMouseDown={(e) => {
                        // Blur any focused input field
                        if (document.activeElement && document.activeElement.tagName === 'INPUT') {
                          document.activeElement.blur();
                        }
                      }}
                      onClick={handleConfirmDetails}
                      style={{ width: "300px"}}
                      disabled={!allRequiredFieldsValid || !consent1 || isProcessing}
                    >
                      {isProcessing ? "Processing..." : "Confirm Details"}
                    </button>
                </div>
                
              </div>
            </div>
          )}

          {/* Step 2: Company Details */}
          {step === 2 && (
            <div className="business-form-section" style={{ width: "100%" }}>
              <h3 style={{ textAlign: "center", marginBottom: 24 }}>Please verify your company and directors info based on GST.</h3>
              <div className="onboard-step" style={{ maxWidth: 900, margin: "0 auto" }}>
                <div className="input-row" style={{ gap: 24, flexWrap: 'wrap', display: 'flex' }}>
                  {/* Company Details Section */}
                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      name="businessType" 
                      value={companyDetails.businessType} 
                      onChange={handleCompanyDetailChange}
                      onFocus={() => handleCompanyFieldFocus('businessType')}
                      onBlur={() => handleCompanyFieldBlur('businessType')}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label" htmlFor="businessType">Business Type</label>
                    {focusedFields.businessType && (
                      <div className="field-hint-container">
                        <span className="field-hint">Type of business entity (e.g., Private Limited, LLP)</span>
                      </div>
                    )}
                  </div>

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      name="legalName" 
                      value={companyDetails.legalName} 
                      onChange={handleCompanyDetailChange}
                      onFocus={() => handleCompanyFieldFocus('legalName')}
                      onBlur={() => handleCompanyFieldBlur('legalName')}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label" htmlFor="legalName">Legal Name</label>
                    {focusedFields.legalName && (
                      <div className="field-hint-container">
                        <span className="field-hint">Registered legal name of your company</span>
                      </div>
                    )}
                  </div>

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      name="registeredAddress" 
                      value={companyDetails.registeredAddress} 
                      onChange={handleCompanyDetailChange}
                      onFocus={() => handleCompanyFieldFocus('registeredAddress')}
                      onBlur={() => handleCompanyFieldBlur('registeredAddress')}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label" htmlFor="registeredAddress">Registered Address</label>
                    {focusedFields.registeredAddress && (
                      <div className="field-hint-container">
                        <span className="field-hint">Official registered address of the company</span>
                      </div>
                    )}
                  </div>

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      name="natureOfWork" 
                      value={companyDetails.natureOfWork} 
                      onChange={handleCompanyDetailChange}
                      onFocus={() => handleCompanyFieldFocus('natureOfWork')}
                      onBlur={() => handleCompanyFieldBlur('natureOfWork')}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label" htmlFor="natureOfWork">Nature of Work</label>
                    {focusedFields.natureOfWork && (
                      <div className="field-hint-container">
                        <span className="field-hint">Primary business activity or industry</span>
                      </div>
                    )}
                  </div>

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      name="annualTurnoverSlab" 
                      value={companyDetails.annualTurnoverSlab} 
                      onChange={handleCompanyDetailChange}
                      onFocus={() => handleCompanyFieldFocus('annualTurnoverSlab')}
                      onBlur={() => handleCompanyFieldBlur('annualTurnoverSlab')}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label" htmlFor="annualTurnoverSlab">Annual Turnover Slab</label>
                    {focusedFields.annualTurnoverSlab && (
                      <div className="field-hint-container">
                        <span className="field-hint">Annual revenue range of your business</span>
                      </div>
                    )}
                  </div>

                 

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      name="gstActive" 
                      value={companyDetails.gstActive} 
                      onChange={handleCompanyDetailChange}
                      onFocus={() => handleCompanyFieldFocus('gstActive')}
                      onBlur={() => handleCompanyFieldBlur('gstActive')}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label" htmlFor="gstActive">GST Active</label>
                    {focusedFields.gstActive && (
                      <div className="field-hint-container">
                        <span className="field-hint">GST registration status (Yes/No)</span>
                      </div>
                    )}
                  </div>

                  {/* Contact Details Section Header */}
                  <div style={{ width: '100%', flexBasis: '100%'}}>
                    <h4 style={{ margin: '0', fontSize: 16, textDecoration: 'underline' }}>Contact Details</h4>
                  </div>

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      name="contactName" 
                      value={companyDetails.contactName} 
                      onChange={handleCompanyDetailChange}
                      onFocus={() => handleCompanyFieldFocus('contactName')}
                      onBlur={() => handleCompanyFieldBlur('contactName')}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label" htmlFor="contactName">Name</label>
                    {focusedFields.contactName && (
                      <div className="field-hint-container">
                        <span className="field-hint">Contact person's full name</span>
                      </div>
                    )}
                  </div>

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="email" 
                      name="contactEmail" 
                      value={companyDetails.contactEmail} 
                      onChange={handleCompanyDetailChange}
                      onFocus={() => handleCompanyFieldFocus('contactEmail')}
                      onBlur={() => handleCompanyFieldBlur('contactEmail')}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label" htmlFor="contactEmail">Email</label>
                    {focusedFields.contactEmail && (
                      <div className="field-hint-container">
                        <span className="field-hint">Valid email address • e.g., contact@company.com</span>
                      </div>
                    )}
                  </div>

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="tel" 
                      name="contactMobile" 
                      value={companyDetails.contactMobile} 
                      onChange={handleCompanyDetailChange}
                      onFocus={() => handleCompanyFieldFocus('contactMobile')}
                      onBlur={() => handleCompanyFieldBlur('contactMobile')}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label" htmlFor="contactMobile">Mobile Number</label>
                    {focusedFields.contactMobile && (
                      <div className="field-hint-container">
                        <span className="field-hint">10-digit mobile number</span>
                      </div>
                    )}
                  </div>

                 

                  {/* Consent Checkbox */}
                  <div className="checkbox-container" style={{ width: '100%', flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <label 
              className="checkbox-item" 
              style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}
              onMouseDown={(e) => {
                isLabelClick.current = true;
                // Blur any focused input field
                if (document.activeElement && document.activeElement.tagName === 'INPUT') {
                  document.activeElement.blur();
                }
                // Toggle state immediately
                setCompanyConsent(prev => !prev);
                e.preventDefault(); // Prevent default to avoid double toggle
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <input
                type="checkbox"
                        checked={companyConsent}
                        onChange={() => {
                          // Only toggle if not triggered by label click
                          if (!isLabelClick.current) {
                            setCompanyConsent(prev => !prev);
                          } else {
                            // Reset flag after onChange
                            setTimeout(() => {
                              isLabelClick.current = false;
                            }, 0);
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        id="companyConsent"
                
              />
              <span>
                 Please check the data properly. It will help in preparing best offer for you.
              </span>
            </label>
          </div>

                {errorMessage && (
                    <div className="field-error" style={{ width: '100%', flexBasis: '100%', marginTop: 8 }}>{errorMessage}</div>
                )}

                  <div style={{ width: '100%', flexBasis: '100%', display: "flex", justifyContent: "center" }}>
                    <button
                      className="business-start-application-btn"
                      onMouseDown={(e) => {
                        // Blur any focused input field
                        if (document.activeElement && document.activeElement.tagName === 'INPUT') {
                          document.activeElement.blur();
                        }
                      }}
                      onClick={handleVerifyCompanyDetails}
                      style={{ width: "300px"}}
                      disabled={!companyConsent || isProcessingStep2}
                    >
                      {isProcessingStep2 ? "Processing..." : "Verify & Continue"}
                    </button>
                </div>
                </div>
              </div>
            </div>
          )}
          {/* Step 3: Check Loan Offer */}
          {step === 3 && (
            <div className="business-form-section" style={{ width: "100%" }}>
              <h3 style={{ textAlign: "center", marginBottom: 24, fontWeight: 'bold' }}>Check the best offer generated for you</h3>
              <div className="onboard-step" style={{ maxWidth: 900, margin: "0 auto" }}>
                <div className="input-row" style={{ gap: 24, flexWrap: 'wrap', display: 'flex' }}>
                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      value={loanOffer.invoiceAmount} 
                      onFocus={() => handleFieldFocus('invoiceAmount')}
                      onBlur={(e) => handleFieldBlur('invoiceAmount', e)}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label">Invoice Amount</label>
                    {focusedFields.invoiceAmount && (
                      <div className="field-hint-container">
                        <span className="field-hint">Total amount of the invoice</span>
                      </div>
                    )}
                </div>
                
                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      value={loanOffer.approvedLoanAmount} 
                      onFocus={() => handleFieldFocus('approvedLoanAmount')}
                      onBlur={(e) => handleFieldBlur('approvedLoanAmount', e)}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label">Approved Loan Amount</label>
                    {focusedFields.approvedLoanAmount && (
                      <div className="field-hint-container">
                        <span className="field-hint">Maximum loan amount approved for you</span>
              </div>
                    )}
                  </div>

                  <div className={`floating-input-group${requiredLoanAmountError ? ' error' : ''}`} style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      value={loanOffer.requiredLoanAmount} 
                      onChange={handleRequiredLoanAmountChange}
                      onFocus={() => handleFieldFocus('requiredLoanAmount')}
                      onBlur={(e) => { handleFieldBlur('requiredLoanAmount', e); handleRequiredLoanAmountBlur(e); }}
                      className="floating-input" 
                      placeholder=" " 
                    />
                    <label className="floating-label">Required Loan Amount</label>
                    {focusedFields.requiredLoanAmount && (
                      <div className="field-hint-container">
                        <span className="field-hint">Minimum: ₹ 100 | Maximum: {loanOffer.approvedLoanAmount}</span>
            </div>
          )}
                    {requiredLoanAmountError && (
                      <div className="field-error">{requiredLoanAmountError}</div>
                    )}
                  </div>

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <DatePicker
                      value={stringToDayjs(loanOffer.expectedPaymentDate)}
                      onChange={handleExpectedPaymentDateChange}
                      onOpen={() => handleFieldFocus('expectedPaymentDate')}
                      onClose={() => handleFieldBlur('expectedPaymentDate', null)}
                      format="YYYY-MM-DD"
                      minDate={dayjs()}
                      slotProps={{
                        textField: {
                          className: "floating-input",
                          placeholder: " ",
                          onFocus: () => handleFieldFocus('expectedPaymentDate'),
                          onBlur: (e) => handleFieldBlur('expectedPaymentDate', e),
                          variant: "standard",
                          InputProps: {
                            disableUnderline: true,
                          },
                          inputProps: {
                            style: {
                              padding: '0',
                              height: '100%',
                              fontSize: '16px',
                            }
                          }
                        }
                      }}
                      sx={{ width: '100%' }}
                    />
                    <label className="floating-label">Expected Date of Payment</label>
                    {focusedFields.expectedPaymentDate && (
                      <div className="field-hint-container">
                        <span className="field-hint">Expected date when payment will be received</span>
              </div>
                    )}
                  </div>

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      value={loanOffer.rateOfInterest} 
                      onFocus={() => handleFieldFocus('rateOfInterest')}
                      onBlur={(e) => handleFieldBlur('rateOfInterest', e)}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label">Rate of Interest (Annually)</label>
                    {focusedFields.rateOfInterest && (
                      <div className="field-hint-container">
                        <span className="field-hint">Annual interest rate applicable on the loan</span>
            </div>
          )}
              </div>

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      value={loanOffer.durationOfLoan} 
                      onFocus={() => handleFieldFocus('durationOfLoan')}
                      onBlur={(e) => handleFieldBlur('durationOfLoan', e)}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label">Duration of Loan</label>
                    {focusedFields.durationOfLoan && (
                      <div className="field-hint-container">
                        <span className="field-hint">Loan tenure in days</span>
            </div>
          )}
                  </div>

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      value={loanOffer.interestToBePaid} 
                      onFocus={() => handleFieldFocus('interestToBePaid')}
                      onBlur={(e) => handleFieldBlur('interestToBePaid', e)}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label">Interest to be Paid</label>
                    {focusedFields.interestToBePaid && (
                      <div className="field-hint-container">
                        <span className="field-hint">Total interest amount payable on the loan</span>
                      </div>
                    )}
                  </div>

                  <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                    <input 
                      type="text" 
                      value={loanOffer.totalAmountToBePaid} 
                      onFocus={() => handleFieldFocus('totalAmountToBePaid')}
                      onBlur={(e) => handleFieldBlur('totalAmountToBePaid', e)}
                      className="floating-input" 
                      placeholder=" " 
                      disabled
                    />
                    <label className="floating-label">Total Amount to be Paid</label>
                    {focusedFields.totalAmountToBePaid && (
                      <div className="field-hint-container">
                        <span className="field-hint">Principal amount + interest to be repaid</span>
                      </div>
                    )}
                  </div>

                  {/* Consent Checkbox */}
                  <div className="checkbox-container" style={{ width: '100%', flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <label 
                      className="checkbox-item" 
                      style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}
                      onMouseDown={(e) => {
                        isLabelClick.current = true;
                        // Blur any focused input field
                        if (document.activeElement && document.activeElement.tagName === 'INPUT') {
                          document.activeElement.blur();
                        }
                        // Toggle state immediately
                        setLoanOfferConsent(prev => !prev);
                        e.preventDefault(); // Prevent default to avoid double toggle
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={loanOfferConsent}
                        onChange={() => {
                          // Only toggle if not triggered by label click
                          if (!isLabelClick.current) {
                            setLoanOfferConsent(prev => !prev);
                          } else {
                            // Reset flag after onChange
                            setTimeout(() => {
                              isLabelClick.current = false;
                            }, 0);
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        id="loanOfferConsent"
                      
                      />
                      <span>
                      I confirm that the details are correct as per my knowledge.
                      </span>
                    </label>
                  </div>

                  {errorMessage && (
                    <div className="field-error" style={{ width: '100%', flexBasis: '100%', marginTop: 8 }}>{errorMessage}</div>
                  )}

                  <div style={{ width: '100%', flexBasis: '100%', display: "flex", justifyContent: "center" }}>
                    <button
                      className="business-start-application-btn"
                      onMouseDown={(e) => {
                        // Blur any focused input field
                        if (document.activeElement && document.activeElement.tagName === 'INPUT') {
                          document.activeElement.blur();
                        }
                      }}
                      onClick={handleCompleteApplication}
                      style={{ width: "300px"}}
                      disabled={!loanOfferConsent || isProcessingStep3}
                    >
                      {isProcessingStep3 ? "Processing..." : "Complete Application"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          

       
    </section>
      )}

    {/* OTP Verification Modal */}
    {showOtpModal && (
      <OtpVerificationModal
        mobile={decryptedMobileNumber}
        productType="Supply Chain Financing"
        show={showOtpModal}
        onClose={() => {
          setShowOtpModal(false);
          setDecryptedMobileNumber(null);
        }}
        onVerified={handleOtpVerified}
      />
    )}

    {/* Notification Popup for Anchor Registration Success */}
    <NotificationPopup
      show={showNotification}
      message={notificationMessage}
      type={notificationType}
      onClose={() => {
        setShowNotification(false);
        navigate('/inventory-financing', { replace: true });
      }}
      autoClose={true}
      autoCloseDelay={5000}
    />

    {/* Notification Popup for Pending Case */}
    <NotificationPopup
      show={showPendingNotification}
      message={<>Our buyer verification is in progress. <br /> Thank you for your patience! We will notify you once the verification is completed.</>}
      type="warning"
      customButton={
        <button
          className="business-start-application-btn"
          onClick={() => {
            setShowPendingNotification(false);
            clearStateFromStorage();
            navigate('/inventory-financing', { replace: true });
          }}
          style={{ width: 'fit-content', minWidth: '200px', marginTop: '24px' ,backgroundColor: '#ffc107', color: '#fff'}}
        >
          OK
        </button>
      }
      onClose={() => {
        setShowPendingNotification(false);
        clearStateFromStorage();
        navigate('/inventory-financing', { replace: true });
      }}
    />

    {/* Notification Popup for Rejected Case */}
    <NotificationPopup
      show={showRejectedNotification}
      message={<>We will not be able to proceed with your application. <br /> The buyer mentioned in the invoice doesn't meet our credit criteria.</>}
      type="error"
      customIcon="😢"
      customButton={
        <button
          className="business-start-application-btn"
          onClick={() => {
            setShowRejectedNotification(false);
            clearStateFromStorage();
            navigate('/inventory-financing', { replace: true });
          }}
          style={{ width: 'fit-content', minWidth: '200px', marginTop: '24px' }}
        >
          I understand
        </button>
      }
      onClose={() => {
        setShowRejectedNotification(false);
        clearStateFromStorage();
        navigate('/inventory-financing', { replace: true });
      }}
      
    />

    {/* Anchor Not Found Popup */}
    {showAnchorNotFoundPopup && (
      <div 
        className="anchor-popup-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '16px'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCloseAnchorNotFoundPopup();
          }
        }}
      >
        <div 
          className="business-form-section anchor-popup-container"
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            width: '60%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            padding: '30px'
          }}
        >
          <style>{`
            @media (max-width: 768px) {
              .anchor-popup-container {
                width: 100% !important;
                padding: 20px !important;
                border-radius: 8px !important;
              }
              .anchor-popup-container .input-row .floating-input-group {
                flex: 0 0 100% !important;
              }
              .anchor-popup-overlay .onboard-step {
                max-width: 85% !important;
                width: 85% !important;
                min-height: fit-content !important;
              }
            }
          `}</style>
          {/* Close Button */}
          <button 
            onClick={handleCloseAnchorNotFoundPopup}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: '#333',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#fff',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              lineHeight: 1,
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#555';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#333';
            }}
          >
            ✕
          </button>

          {/* Header */}
         
          <p style={{ textAlign: 'center', marginBottom: '8px', color: '#333', fontWeight: '500' }}>
            {anchorStatusMessage}
          </p>
          <p style={{ textAlign: 'center', marginBottom: '24px', color: '#666', lineHeight: '1.6' }}>
          You can help them to register by providing the following details.
          </p>

          {/* Form Fields */}
          <div className="onboard-step" style={{ maxWidth: '100%', margin: '0 auto' }}>
            <div className="input-row" style={{ gap: '16px 24px', flexWrap: 'wrap', display: 'flex' }}>
              
              {/* Row 1: Company Name & GST */}
              <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                <input 
                  type="text" 
                  name="buyerGst" 
                  value={buyerDetails.buyerGst} 
                  onChange={handleBuyerDetailChange}
                  onFocus={() => handleFieldFocus('popup_buyerGst')}
                  onBlur={(e) => handleFieldBlur('popup_buyerGst', e)}
                  className="floating-input" 
                  placeholder=" " 
                  maxLength={15}
                  style={{ textTransform: 'uppercase' }}
                />
                <label className="floating-label">Buyer GST Number</label>
                {focusedFields.popup_buyerGst && (
                  <div className="field-hint-container">
                    <span className="field-hint">15-digit GSTIN • e.g., 22AAAAA0000A1Z5</span>
                  </div>
                )}
                {buyerDetailsErrors.buyerGst && (
                  <div className="field-error">{buyerDetailsErrors.buyerGst}</div>
                )}
              </div>


              <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                <input 
                  type="text" 
                  name="companyName" 
                  value={buyerDetails.companyName} 
                  onChange={handleBuyerDetailChange}
                  onFocus={() => handleFieldFocus('popup_companyName')}
                  onBlur={(e) => handleFieldBlur('popup_companyName', e)}
                  className="floating-input" 
                  placeholder=" " 
                  maxLength={100}
                />
                <label className="floating-label">Buyer Company Name</label>
                {focusedFields.popup_companyName && (
                  <div className="field-hint-container">
                    <span className="field-hint">Enter buyer's registered company name</span>
                  </div>
                )}
                {buyerDetailsErrors.companyName && (
                  <div className="field-error">{buyerDetailsErrors.companyName}</div>
                )}
              </div>

              
              <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                <input 
                  type="text" 
                  name="contactPersonName" 
                  value={buyerDetails.contactPersonName} 
                  onChange={handleBuyerDetailChange}
                  onFocus={() => handleFieldFocus('popup_contactPersonName')}
                  onBlur={(e) => handleFieldBlur('popup_contactPersonName', e)}
                  className="floating-input" 
                  placeholder=" " 
                  maxLength={50}
                />
                <label className="floating-label">Contact Person Name</label>
                {focusedFields.popup_contactPersonName && (
                  <div className="field-hint-container">
                    <span className="field-hint">Full name of the contact person</span>
                  </div>
                )}
                {buyerDetailsErrors.contactPersonName && (
                  <div className="field-error">{buyerDetailsErrors.contactPersonName}</div>
                )}
              </div>

              {/* Row 3: Mobile */}
              <div className="floating-input-group" style={{ flex: '0 0 48%' }}>
                <input 
                  type="tel" 
                  name="contactMobile" 
                  value={buyerDetails.contactMobile} 
                  onChange={handleBuyerDetailChange}
                  onFocus={() => handleFieldFocus('popup_contactMobile')}
                  onBlur={(e) => handleFieldBlur('popup_contactMobile', e)}
                  className="floating-input" 
                  placeholder=" " 
                  maxLength={10}
                  inputMode="numeric"
                />
                <label className="floating-label">Contact Mobile Number</label>
                {focusedFields.popup_contactMobile && (
                  <div className="field-hint-container">
                    <span className="field-hint">10-digit mobile number • e.g., 9876543210</span>
                  </div>
                )}
                {buyerDetailsErrors.contactMobile && (
                  <div className="field-error">{buyerDetailsErrors.contactMobile}</div>
                )}
              </div>
            </div>

            {errorMessage && (
              <div className="field-error" style={{ textAlign: 'center', marginTop: '16px' }}>
                {errorMessage}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '24px' }}>
             
              <button
                className="business-start-application-btn"
                onClick={handleSubmitBuyerDetails}
                disabled={isSubmittingBuyerDetails}
                style={{ width: 'fit-content' }}
              >
                {isSubmittingBuyerDetails ? "Initiating..." : "Initiate Registration"}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Thank You Page */}
    {showThankYou && (
      <section className="onboard-container" style={{ marginBottom: 40 }}>
        <div className="inventory-thank-you-page">
          <div className="inventory-thank-you-container">
            <h1 className="inventory-thank-you-title">Thank You!</h1>
            <p className="inventory-thank-you-message">
              Your loan application has been successfully submitted.
            </p>
            <p className="inventory-thank-you-description">
              We have received your application and will process it shortly. Our team will review your details and get back to you within 24-48 hours.
            </p>

            <div className="inventory-application-details-card">
              <h3 className="inventory-application-details-title">Application Details</h3>
              <div className="inventory-application-details-content inventory-application-details-single-row">
                <div className="inventory-application-details-item">
                  <span className="inventory-application-details-label">Application ID</span>
                  <span className="inventory-application-details-value">{leadId || "N/A"}</span>
                </div>
                <div className="inventory-application-details-item">
                  <span className="inventory-application-details-label">Status</span>
                  <span className="inventory-application-details-status">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px' }}>
                      <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm.5-9H7v5l4.25 2.5.75-1.23L8.5 8.5V5z" fill="#FF9800"/>
                    </svg>
                    Submitted
                  </span>
                </div>
                <div className="inventory-application-details-item">
                  <span className="inventory-application-details-label">Submitted On</span>
                  <span className="inventory-application-details-value">{submittedDate}</span>
                </div>
              </div>
            </div>

            <p className="inventory-redirect-message">
              Redirecting to homepage in <span className="inventory-redirect-countdown">{redirectCountdown}</span> seconds
            </p>
            <p style={{ textAlign: 'center', marginTop: '16px' }}>
              <a 
                href="/inventory-financing" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/inventory-financing', { replace: true });
                }}
                style={{ 
                  color: '#20386F', 
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                Click here to go to homepage
              </a>
            </p>
          </div>
        </div>
      </section>
    )}

    <Footer/>
    </LocalizationProvider>
  );
};

export default InventoryFinancingApplicationPage;
